import { useForm } from '@folklore/forms';
import { getComponentFromName } from '@folklore/utils';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'wouter';

import { useFormSubmission, useSubmitFormSubmission } from '../../hooks/useSubmissions';
import getFieldsFromForm from '../../lib/getFieldsFromForm';
import getVisibleFieldsFromForm from '../../lib/getVisibleFieldsFromForm';

import Blocks from '../blocks/Blocks';
import FormButton from '../buttons/FormButton';
import * as FieldComponents from '../fields/index';
import FormStatus from '../partials/FormStatus';
import FormDescription from '../typography/FormDescription';
import FormControl from './FormControl';

import styles from '../../styles/forms/form.module.css';

interface FormProps {
    form: Form;
    onComplete?: ((user: Record<string, unknown>) => void) | null;
    className?: string | null;
}

function Form({ form, onComplete: customOnComplete = null, className = null }: FormProps) {
    const { id: formId, description, results } = form;
    const { blocks: resultsBlocks = null } = results || {};
    const formFields = getFieldsFromForm(form);
    const fieldNames = (formFields || []).map(({ name }) => name) as string[];
    const [location] = useLocation();
    const { submission = null, isLoading: submissionIsLoading } = useFormSubmission(formId);
    const [completed, setCompleted] = useState(submission !== null);

    const onComplete = (newUser: Record<string, unknown>) => {
        setCompleted(true);
        if (customOnComplete !== null) {
            customOnComplete(newUser);
        }
    };

    const { submitAsync } = useSubmitFormSubmission(formId);

    const postForm = (postAction: string, postData): Promise<SubmissionMutationResponse> =>
        submitAsync({
            url: `${window.location.protocol}//${window.location.host}${location}`,
            ...postData,
        });

    const { fields, value, onSubmit, status, errors } = useForm({
        fields: fieldNames,
        onComplete,
        postForm,
    });
    // The answers of the questions hidden by their show condition are ignored by the API
    const visibleFields = getVisibleFieldsFromForm(form, value);

    useEffect(() => {
        if (!submissionIsLoading && submission !== null) {
            setCompleted(true);
        }
    }, [submissionIsLoading]);

    return (
        <form
            method="post"
            className={classNames([
                styles.container,
                {
                    'was-validated': errors !== null,
                },
                className,
            ])}
            onSubmit={onSubmit}
        >
            {completed ? (
                <div className={styles.success}>
                    {resultsBlocks !== null ? (
                        <Blocks blocks={resultsBlocks} withoutMaxWidth />
                    ) : (
                        <FormattedMessage
                            defaultMessage="Merci d’avoir rempli rempli le formulaire."
                            description="Form success message"
                        />
                    )}
                </div>
            ) : (
                <>
                    {description !== null ? (
                        <FormDescription
                            tag="div"
                            html={description}
                            className={styles.description}
                        />
                    ) : null}
                </>
            )}

            {!completed ? (
                <div className={styles.fields}>
                    {visibleFields.map(
                        ({ type: fieldType, component: fieldComponent, name, label, ...field }) => {
                            const FieldComponent =
                                getComponentFromName(FieldComponents, fieldComponent) ||
                                getComponentFromName(FieldComponents, fieldType);
                            return FieldComponent !== null ? (
                                <FormControl
                                    key={`field-${name}`}
                                    label={label as string}
                                    {...fields[name]}
                                    withoutErrors
                                    className={classNames([styles.formControl, styles.formRow])}
                                    labelClassName={styles.label}
                                >
                                    <FieldComponent
                                        {...fields[name]}
                                        {...field}
                                        withBorder
                                        className={styles.field}
                                    />
                                </FormControl>
                            ) : null;
                        },
                    )}

                    <div className={styles.actions}>
                        <FormStatus status={status} className={styles.status} />
                        <FormButton
                            type="submit"
                            className={styles.submitButton}
                            disabled={status === 'loading'}
                        >
                            <FormattedMessage defaultMessage="Envoyer" description="Button label" />
                        </FormButton>
                    </div>
                </div>
            ) : null}
        </form>
    );
}

export default Form;
